import { useRef } from "react";
import { Link } from "react-router-dom";
import { carouselProducts, slugify } from "../data";

/* ─── Carousel ─────────────────────────────────────────────────────────────── */
function ProductCarousel() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth"
      });
    }
  };

  return (
    <div className="relative group">
      {/* Scroll container */}
      <div 
        ref={scrollRef}
        className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-6 hide-scrollbar"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <style>{`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
        `}</style>
        
        {carouselProducts.map((item) => (
          <div 
            key={item.name} 
            className="snap-start shrink-0 w-[85%] sm:w-[calc(50%-12px)] lg:w-[calc(33.333333%-16px)] bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-xl hover:border-teal-200 transition-all duration-300 group/card flex flex-col"
          >
            {/* Image */}
            <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
              <img 
                src={item.image} 
                alt={item.name} 
                width={800} 
                height={600} 
                loading="lazy" 
                className="h-full w-full object-cover transition-transform duration-700 group-hover/card:scale-105" 
              />
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-slate-700 shadow-sm border border-white/20">
                {item.category}
              </div>
            </div>

            {/* Content */}
            <div className="p-6 flex flex-col flex-grow">
              <h3 className="text-xl font-bold text-slate-900 mb-2 line-clamp-2">{item.name}</h3>
              <p className="text-slate-600 text-sm mb-4 line-clamp-3">{item.detail}</p>
              
              <div className="mt-auto pt-4 flex items-center justify-between border-t border-slate-100">
                <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded">
                  {item.specs}
                </span>
                <Link to={`/product/${slugify(item.name)}`} className="text-teal-600 font-semibold text-sm hover:text-teal-700 inline-flex items-center gap-1">
                  View Details
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Buttons */}
      <button 
        onClick={() => scroll("left")}
        className="absolute top-[40%] -left-5 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-700 shadow-[0_0_15px_rgba(0,0,0,0.1)] border border-slate-100 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-50 hover:text-teal-600 z-10 hidden sm:flex"
        aria-label="Previous"
      >
        <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button 
        onClick={() => scroll("right")}
        className="absolute top-[40%] -right-5 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-700 shadow-[0_0_15px_rgba(0,0,0,0.1)] border border-slate-100 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-50 hover:text-teal-600 z-10 hidden sm:flex"
        aria-label="Next"
      >
        <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}

/* ─── Section ───────────────────────────────────────────────────────────────── */
export function Products() {
  return (
    <section id="solutions" className="relative bg-slate-50 py-24">
      <div className="mx-auto max-w-7xl px-6">
        {/* Section header */}
        <div className="mb-12 flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-teal-600">
              Global Product Ecosystem
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Our Product Range
            </h2>
            <p className="mt-3 text-base text-slate-500">
              From mobile batching plants and concrete mixers to construction
              equipment and material handling — complete machinery solutions for
              every project scale.
            </p>
          </div>
          <Link
            to="/products"
            className="shrink-0 inline-flex items-center gap-2 rounded-md border border-teal-600 px-5 py-2 text-sm font-bold text-teal-600 transition hover:bg-teal-600 hover:text-white"
          >
            View All Products
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14m-6-6 6 6-6 6" />
            </svg>
          </Link>
        </div>

        {/* Carousel */}
        <ProductCarousel />
      </div>
    </section>
  );
}
